import React from "react";
import {
  Building2,
  Check,
  FileSearch,
  Minus,
  Presentation,
  Scale,
  Server,
  Users,
  Zap,
} from "lucide-react";
import MarketingLayout from "../components/marketing/MarketingLayout";
import {
  Em,
  Eyebrow,
  GhostButton,
  GradientButton,
  Headline,
  Panel,
  Reveal,
  Section,
  SERIF,
  Subhead,
} from "../components/marketing/primitives";
import { usePageMeta } from "../components/marketing/usePageMeta";

/**
 * Pricing page. Assess-first, adoption-led (August 2026 model), in the warm
 * editorial system shared with the landing page.
 *
 * The commercial structure: a one-off $1,500 code security risk assessment
 * (plus optional $500-1,500 report/IC support) as the low-friction entry for
 * M&A and underwriting; two scoped services added September 2026 (third-party
 * software assurance from $5,000 and a managed code risk assessment service
 * from $2,500, both priced by code size, scanning frequency and term); a free
 * Community tier; then three self-hosted packages priced around the software
 * estate: Starter $199, Growth $499, Large Enterprise $999 per month. No
 * per-seat pricing anywhere. SaaS pricing is deliberately "to be announced".
 *
 * Self-hosted delivery means local code scanning inside the customer's
 * environment with an Azure-hosted processing, analytics and reporting
 * backend: source code stays local and is never uploaded to the SaaS side.
 *
 * Eyebrows are rationed to three (packages, comparison, FAQ). One CTA label
 * per intent: "Book an assessment", "Request a quote", "Start free scan",
 * "Get <plan>". Quote-shaped CTAs go to the /inquiry form.
 */
export default function Pricing() {
  usePageMeta(
    "Pricing | Paxley, never per seat",
    "A $1,500 one-off code risk assessment, scoped assurance services, a free Community tier, and packages from $199 a month. Up to 90% less than per-seat AppSec tools."
  );
  return (
    <MarketingLayout>
      <HeroSection />
      <AssessmentSection />
      <ServicesSection />
      <PlanCardsSection />
      <SavingsSection />
      <WhyThisPricingSection />
      <ComparisonSection />
      <FAQSection />
      <FinalCTA />
    </MarketingLayout>
  );
}

const ASSESSMENT_LINK = "/inquiry?interest=assessment";

// ──────────────────────────────────────────────────────────────────
// Hero
// ──────────────────────────────────────────────────────────────────

function HeroSection() {
  return (
    <Section padding="hero" container="narrow">
      <div className="text-center">
        <Headline size="hero" className="mx-auto">
          Assess first. <Em>Adopt when ready.</Em>
        </Headline>
        <Subhead size="large" className="mx-auto mt-6 max-w-xl">
          A <strong className="font-semibold opacity-100">$1,500 one-off assessment</strong>, or
          a free install. Self-hosted packages run{" "}
          <strong className="font-semibold opacity-100">$199 to $999 a month</strong>, never per
          seat.
        </Subhead>
      </div>
    </Section>
  );
}

// ──────────────────────────────────────────────────────────────────
// One-off assessment entry point
// ──────────────────────────────────────────────────────────────────

function AssessmentSection() {
  return (
    <Section padding="tight">
      <div className="grid gap-5 lg:grid-cols-2">
        <Reveal className="h-full">
          <div className="relative flex h-full flex-col rounded-2xl border-2 border-[#1E4D38] bg-white p-7 shadow-[0_16px_48px_-24px_rgba(30,77,56,0.45)]">
            <span className="absolute -top-3 right-6 rounded-full border border-[#17150F]/70 bg-[#1E4D38] px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-[#F5F1E6]">
              Start here
            </span>
            <FileSearch className="h-5 w-5 text-[#1E4D38]" />
            <h3 className="mt-4 text-lg font-semibold">One-Off Code Security Risk Assessment</h3>
            <div className="mt-2 flex items-baseline gap-1.5">
              <span className="text-4xl" style={SERIF}>
                $1,500
              </span>
              <span className="text-sm opacity-50">per project, one-off</span>
            </div>
            <p className="mt-3 text-sm leading-relaxed opacity-75">
              A focused, point-in-time code security risk view for M&amp;A due diligence, investor
              underwriting, or vendor risk review, with{" "}
              <strong className="font-medium opacity-100">source code never exposed</strong> outside
              the controlled environment. No platform contract, no lock-in.
            </p>
            <ul className="mt-5 space-y-2.5 text-sm">
              {[
                "SAST, dependency and supply-chain, secrets, IaC and container findings",
                "SBOM and license posture for the assessed codebase",
                "Target framework and runtime support status",
                "Diligence-ready findings report",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2.5">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#1E4D38]" />
                  <span className="opacity-85">{f}</span>
                </li>
              ))}
            </ul>
            <div className="mt-auto pt-7">
              <GradientButton to={ASSESSMENT_LINK} className="w-full justify-center">
                Book an assessment
              </GradientButton>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.08} className="h-full">
          <div className="flex h-full flex-col rounded-2xl border border-[#E4DFD3] bg-white p-7">
            <Presentation className="h-5 w-5 text-[#1E4D38]" />
            <h3 className="mt-4 text-lg font-semibold">Custom Report + IC / Underwriting Support</h3>
            <div className="mt-2 flex items-baseline gap-1.5">
              <span className="text-4xl" style={SERIF}>
                $500-1,500
              </span>
              <span className="text-sm opacity-50">one-off</span>
            </div>
            <p className="mt-3 text-sm leading-relaxed opacity-75">
              Builds on the assessment when the audience is an investment committee or an
              underwriter: custom report development, presentation-ready material, and time with
              the team that ran the analysis.
            </p>
            <ul className="mt-5 space-y-2.5 text-sm">
              {[
                "Custom report tailored to the transaction",
                "Investment-committee presentation support",
                "Underwriting discussion with the assessment team",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2.5">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#1E4D38]" />
                  <span className="opacity-85">{f}</span>
                </li>
              ))}
            </ul>
            <div className="mt-auto pt-7">
              <GhostButton
                href="mailto:yash.sancheti@paxleysoftware.com?subject=Custom%20Report%20%2F%20IC%20Support"
                className="w-full justify-center"
              >
                Ask about report support
              </GhostButton>
            </div>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}

// ──────────────────────────────────────────────────────────────────
// Scoped services: third-party software assurance and the managed service.
// "From" prices, confirmed against scope (code size, frequency, term).
// ──────────────────────────────────────────────────────────────────

function ServicesSection() {
  const services = [
    {
      icon: <Building2 className="h-5 w-5 text-[#1E4D38]" />,
      name: "Third-Party Software Assurance",
      price: "from $5,000",
      who: "CIOs and CTOs buying software from third-party companies",
      body: "Paxley scans the vendor's software before you accept it, and again as it changes, so every release you take on is assessed against the same evidence standard as your own code.",
      features: [
        "Pre-acceptance scan of each vendor deliverable",
        "Rescans on every change or on a schedule you set",
        "Findings, dependency and license exposure, SBOM and runtime support status per release",
        "Evidence pack you can hold the vendor to",
      ],
      interest: "third-party-assurance",
    },
    {
      icon: <Server className="h-5 w-5 text-[#1E4D38]" />,
      name: "Managed Code Risk Assessment Service",
      price: "from $2,500",
      who: "Teams that do not want to buy and install software",
      body: "We run Paxley for you. Your team hands over access or an archive, and receives prioritized findings, fix guidance and the reports, with no platform to operate.",
      features: [
        "Scanning run and triaged by the Paxley team",
        "Once, monthly, quarterly or continuous cadence",
        "Prioritized findings with fix guidance, SBOM and reports each cycle",
        "Optional review call with the analysts",
      ],
      interest: "managed-assessment",
    },
  ];

  return (
    <Section padding="tight">
      <Reveal className="mx-auto max-w-3xl text-center">
        <Headline size="section">
          Scanned before you sign. <Em>Or managed for you.</Em>
        </Headline>
        <Subhead className="mx-auto mt-4 max-w-2xl">
          Two services priced by scope: the size of the code, how often it is scanned, and the
          contract length (once, monthly, quarterly or annual).
        </Subhead>
      </Reveal>

      <div className="mt-12 grid gap-5 lg:grid-cols-2">
        {services.map((s, i) => (
          <Reveal key={s.name} delay={0.08 * i} className="h-full">
            <div className="flex h-full flex-col rounded-2xl border border-[#E4DFD3] bg-white p-7">
              {s.icon}
              <h3 className="mt-4 text-lg font-semibold">{s.name}</h3>
              <div className="mt-2 flex items-baseline gap-1.5">
                <span className="text-4xl" style={SERIF}>
                  {s.price}
                </span>
                <span className="text-sm opacity-50">subject to scope</span>
              </div>
              <p className="mt-2 text-sm opacity-60">{s.who}</p>
              <p className="mt-3 text-sm leading-relaxed opacity-75">{s.body}</p>
              <ul className="mt-5 space-y-2.5 text-sm">
                {s.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#1E4D38]" />
                    <span className="opacity-85">{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-7">
                <GhostButton to={`/inquiry?interest=${s.interest}`} className="w-full justify-center">
                  Request a quote
                </GhostButton>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

// ──────────────────────────────────────────────────────────────────
// Recurring plan cards
// ──────────────────────────────────────────────────────────────────

interface Plan {
  name: string;
  price: string;
  cadence: string;
  contract?: string;
  best: string;
  features: string[];
  cta: { label: string; to?: string; href?: string };
  highlight?: boolean;
  badge?: string;
}

function PlanCardsSection() {
  const plans: Plan[] = [
    {
      name: "Community",
      price: "Free",
      cadence: "",
      best: "Open source, solo developers, and initial evaluation",
      features: [
        "3 repositories",
        "5 scans per month",
        "SAST, SCA and secrets",
        "PR guard with inline comments",
        "Community support",
      ],
      cta: { label: "Start free scan", to: "/onboarding" },
    },
    {
      name: "Starter",
      price: "$199",
      cadence: "/month",
      contract: "3, 6 or 12-month terms",
      best: "Small teams with up to 5 products or main repositories",
      features: [
        "Up to 5 products or main repos",
        "150 MB source processing per repo",
        "Full scanning stack: SAST, SCA, secrets, IaC, containers",
        "SBOM and license governance",
        "Self-hosted: code stays with you",
      ],
      cta: { label: "Get Starter", to: "/inquiry?interest=starter" },
    },
    {
      name: "Growth",
      price: "$499",
      cadence: "/month",
      contract: "6-month minimum",
      best: "Medium teams with 5 to 10 products or main repositories",
      features: [
        "5 to 10 products or main repos",
        "500 MB source processing per repo",
        "Everything in Starter",
        "Governance and assurance reporting",
        "Priority support",
      ],
      cta: { label: "Get Growth", to: "/inquiry?interest=growth" },
      highlight: true,
      badge: "Most popular",
    },
    {
      name: "Large Enterprise",
      price: "$999",
      cadence: "/month",
      contract: "12-month minimum",
      best: "Unlimited usage across large teams and broader estates",
      features: [
        "Unlimited products and repositories",
        "Unlimited source processing",
        "Everything in Growth",
        "M&A and diligence evidence outputs",
        "Dedicated support",
      ],
      cta: { label: "Request a quote", to: "/inquiry?interest=enterprise" },
    },
  ];

  return (
    <Section padding="tight">
      <Reveal className="mx-auto max-w-3xl text-center">
        <Eyebrow>Self-hosted packages</Eyebrow>
        <Headline size="section" className="mt-4">
          Priced around your software estate. <Em>Not your headcount.</Em>
        </Headline>
        <Subhead className="mx-auto mt-4 max-w-2xl">
          Self-hosted delivery: local code scanning within your controlled environment, integrated
          with a secure, compliant Azure-hosted processing, analytics and reporting backend. Source
          code stays local and is never uploaded to or exposed within the SaaS environment.
        </Subhead>
      </Reveal>

      <div className="mt-12 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {plans.map((p, i) => (
          <Reveal key={p.name} delay={0.06 * i} className="h-full">
            <div
              className={`relative flex h-full flex-col rounded-2xl border bg-white p-6 transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] hover:-translate-y-1 hover:shadow-[0_16px_44px_-22px_rgba(30,77,56,0.3)] ${
                p.highlight
                  ? "border-[#1E4D38] shadow-[0_10px_36px_-16px_rgba(30,77,56,0.4)]"
                  : "border-[#E4DFD3]"
              }`}
            >
              {p.badge && (
                <span className="absolute -top-3 right-6 rounded-full border border-[#17150F]/70 bg-[#1E4D38] px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-[#F5F1E6]">
                  {p.badge}
                </span>
              )}
              <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] opacity-50">
                {p.name}
              </h3>
              <div className="mt-3 flex items-baseline gap-1.5">
                <span className="text-4xl" style={SERIF}>
                  {p.price}
                </span>
                {p.cadence && <span className="text-sm opacity-50">{p.cadence}</span>}
              </div>
              {p.contract && (
                <p className="mt-2 inline-flex self-start rounded-full border border-[#1E4D38]/25 bg-[#E4EFE4] px-3 py-1 text-xs font-medium text-[#1E4D38]">
                  {p.contract}
                </p>
              )}
              <p className="mt-2 text-sm opacity-60">{p.best}</p>

              <ul className="mt-6 space-y-2.5 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#1E4D38]" />
                    <span className="opacity-85">{f}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-auto pt-8">
                {p.cta.to ? (
                  <GhostButton to={p.cta.to} className="w-full justify-center">
                    {p.cta.label}
                  </GhostButton>
                ) : p.highlight ? (
                  <GradientButton href={p.cta.href} className="w-full justify-center">
                    {p.cta.label}
                  </GradientButton>
                ) : (
                  <GhostButton href={p.cta.href} className="w-full justify-center">
                    {p.cta.label}
                  </GhostButton>
                )}
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal>
        <p className="mx-auto mt-8 max-w-3xl text-center text-sm opacity-60">
          Payment types: card through secure checkout for Starter and Growth; invoice and bank
          transfer for Large Enterprise, the assessment and the services, with annual prepayment
          available. SaaS pricing will be announced separately.
        </p>
      </Reveal>
    </Section>
  );
}

// ──────────────────────────────────────────────────────────────────
// Savings: the worked example from the differentiation brief, as a
// three-figure equation on a white panel
// ──────────────────────────────────────────────────────────────────

function SavingsSection() {
  return (
    <Section padding="tight">
      <Reveal>
        <div className="rounded-2xl border border-[#E4DFD3] bg-white p-7 md:p-10">
          <div className="grid gap-8 md:grid-cols-[1.2fr_2fr] md:items-center">
            <div>
              <Headline size="small">
                Up to 90% less than <Em>per-seat tools.</Em>
              </Headline>
              <p className="mt-3 text-sm leading-relaxed opacity-70">
                Example: 50 developers working across 10 repositories. Starter works out at about
                $40 per repository; Growth covers all ten.
              </p>
            </div>
            <dl className="grid grid-cols-3 gap-px overflow-hidden rounded-xl border border-[#E4DFD3] bg-[#E4DFD3] text-center">
              {[
                { k: "~$4,750", v: "a month on a per-seat stack, about $95 per developer" },
                { k: "$499", v: "a month on Paxley Growth, every developer included" },
                { k: "89%", v: "less, with SBOM, IaC, containers and licences added" },
              ].map((f, i) => (
                <div key={f.k} className={i === 2 ? "bg-[#1E4D38] px-3 py-5 text-[#F5F1E6]" : "bg-[#FBF8F1] px-3 py-5"}>
                  <dt className="text-2xl md:text-3xl" style={SERIF}>
                    {f.k}
                  </dt>
                  <dd className="mt-1 text-[11px] leading-snug opacity-70 md:text-xs">{f.v}</dd>
                </div>
              ))}
            </dl>
          </div>
          <p className="mt-6 text-xs opacity-50">
            Per-seat figure is an estimate for a typical combination of per-developer AppSec tools.
            Paxley prices are current market-entry prices and may change after broader launch.
          </p>
        </div>
      </Reveal>
    </Section>
  );
}

// ──────────────────────────────────────────────────────────────────
// Why this pricing: green panel, two columns of reasons on hairlines
// ──────────────────────────────────────────────────────────────────

function WhyThisPricingSection() {
  const points = [
    {
      icon: <FileSearch className="h-4 w-4" />,
      title: "Affordable entry point",
      body: "M&A, investment and underwriting teams can start with a $1,500 code security risk assessment: a focused risk decision before any platform commitment.",
    },
    {
      icon: <Users className="h-4 w-4" />,
      title: "No per-seat tax",
      body: "You pay for the product and repository estate being protected, not for every developer, contractor, auditor or occasional contributor.",
    },
    {
      icon: <Server className="h-4 w-4" />,
      title: "Source-code control",
      body: "Self-hosted delivery scans code locally inside your environment; only analysis results reach the Azure-hosted backend. Built for M&A, regulated and IP-sensitive contexts where source cannot leave.",
    },
    {
      icon: <Zap className="h-4 w-4" />,
      title: "Clear upgrade path",
      body: "Free, then Starter, Growth and Enterprise: a practical adoption journey from first evaluation to continuous assurance across the estate.",
    },
    {
      icon: <Building2 className="h-4 w-4" />,
      title: "Enterprise value at SMB pricing",
      body: "Large Enterprise is $999 a month for self-hosted unlimited use, far below typical quote-led enterprise AppSec contracts.",
    },
    {
      icon: <Scale className="h-4 w-4" />,
      title: "One platform, not five tools",
      body: "SAST, SCA, secrets, IaC, containers, SBOM, license compliance, code quality and governance reporting in one package instead of a stack of point solutions.",
    },
  ];

  return (
    <Section container="none" padding="tight">
      <Panel tone="green">
        <div className="mx-auto max-w-6xl px-6 py-16 sm:px-10 md:py-20">
          <Reveal className="max-w-2xl">
            <Headline size="section">
              Simple, assess-first, <Em>adoption-led.</Em>
            </Headline>
          </Reveal>

          <div className="mt-12 grid gap-x-12 md:grid-cols-2">
            {points.map((s, i) => (
              <Reveal key={s.title} delay={0.04 * i}>
                <div className="border-t border-white/15 py-6">
                  <div className="flex items-center gap-2.5">
                    <span className="text-[#F5F1E6]/70">{s.icon}</span>
                    <h3 className="text-base font-semibold">{s.title}</h3>
                  </div>
                  <p className="mt-2.5 text-sm leading-relaxed opacity-70">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Panel>
    </Section>
  );
}

// ──────────────────────────────────────────────────────────────────
// Comparison matrix
// ──────────────────────────────────────────────────────────────────

function ComparisonSection() {
  const rows: Array<{
    category?: string;
    label: string;
    community: React.ReactNode;
    starter: React.ReactNode;
    growth: React.ReactNode;
    enterprise: React.ReactNode;
  }> = [
    { category: "Scope", label: "Products / main repositories", community: "3", starter: "Up to 5", growth: "5 to 10", enterprise: "Unlimited" },
    { label: "Source processing per repository", community: "10 MB", starter: "150 MB", growth: "500 MB", enterprise: "Unlimited" },
    { label: "Scans", community: "5 / month", starter: "Continuous", growth: "Continuous", enterprise: "Continuous" },
    { label: "Contract", community: "None", starter: "3, 6 or 12 months", growth: "6-month minimum", enterprise: "12-month minimum" },
    { label: "Deployment", community: "Hosted evaluation", starter: "Self-hosted", growth: "Self-hosted", enterprise: "Self-hosted" },

    { category: "Code security", label: "Static analysis (SAST)", community: <CellYes />, starter: <CellYes />, growth: <CellYes />, enterprise: <CellYes /> },
    { label: "Dependency and supply-chain analysis (SCA)", community: <CellYes />, starter: <CellYes />, growth: <CellYes />, enterprise: <CellYes /> },
    { label: "Secrets detection", community: <CellYes />, starter: <CellYes />, growth: <CellYes />, enterprise: <CellYes /> },
    { label: "IaC and container scanning", community: <CellNo />, starter: <CellYes />, growth: <CellYes />, enterprise: <CellYes /> },
    { label: "SBOM generation and license compliance", community: <CellNo />, starter: <CellYes />, growth: <CellYes />, enterprise: <CellYes /> },
    { label: "Code quality and maintainability", community: <CellNo />, starter: <CellYes />, growth: <CellYes />, enterprise: <CellYes /> },
    { label: "Priority and risk scoring (EPSS, KEV)", community: <CellNo />, starter: <CellYes />, growth: <CellYes />, enterprise: <CellYes /> },

    { category: "Assurance and governance", label: "Governance reporting", community: <CellNo />, starter: <CellNo />, growth: <CellYes />, enterprise: <CellYes /> },
    { label: "M&A and diligence evidence outputs", community: <CellNo />, starter: <CellNo />, growth: <CellYes />, enterprise: <CellYes /> },
    { label: "Custom rules and baseline scheduling", community: <CellNo />, starter: <CellYes />, growth: <CellYes />, enterprise: <CellYes /> },
    { label: "CSV, JSON and report export", community: <CellNo />, starter: <CellYes />, growth: <CellYes />, enterprise: <CellYes /> },
    { label: "Dedicated support", community: <CellNo />, starter: <CellNo />, growth: <CellNo />, enterprise: <CellYes /> },
  ];

  return (
    <Section>
      <Reveal className="mx-auto max-w-3xl text-center">
        <Eyebrow>Compare packages</Eyebrow>
        <Headline size="section" className="mt-4">
          Every package, <Em>side by side.</Em>
        </Headline>
      </Reveal>

      <div className="mt-12 overflow-hidden rounded-2xl border border-[#E4DFD3] bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E4DFD3] bg-[#FBF8F1]">
                <th className="px-6 py-4 text-left text-[11px] font-semibold uppercase tracking-widest opacity-50">
                  Capability
                </th>
                {["Community", "Starter", "Growth", "Enterprise"].map((h) => (
                  <th
                    key={h}
                    className={`px-6 py-4 text-center text-[11px] font-semibold uppercase tracking-widest ${
                      h === "Growth" ? "text-[#1E4D38]" : "opacity-50"
                    }`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <React.Fragment key={`${r.label}-${i}`}>
                  {r.category && (
                    <tr className="bg-[#E4EFE4]/60">
                      <td
                        colSpan={5}
                        className="px-6 py-2.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#1E4D38]"
                      >
                        {r.category}
                      </td>
                    </tr>
                  )}
                  <tr className="border-t border-[#E4DFD3]/70">
                    <td className="px-6 py-3 opacity-85">{r.label}</td>
                    <td className="px-6 py-3 text-center opacity-60">{r.community}</td>
                    <td className="px-6 py-3 text-center opacity-60">{r.starter}</td>
                    <td className="px-6 py-3 text-center font-medium">{r.growth}</td>
                    <td className="px-6 py-3 text-center opacity-60">{r.enterprise}</td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Section>
  );
}

function CellYes() {
  return <Check className="mx-auto h-4 w-4 text-[#1E4D38]" aria-label="Included" />;
}
function CellNo() {
  return <Minus className="mx-auto h-4 w-4 opacity-25" aria-label="Not included" />;
}

// ──────────────────────────────────────────────────────────────────
// FAQ: borderless accordion, one hairline per row
// ──────────────────────────────────────────────────────────────────

function FAQSection() {
  const faqs = [
    {
      q: "Do you charge per developer?",
      a: "No. Paxley is priced around the software estate being protected (the products and repositories), not per seat. Invite every developer, contractor, and auditor at no extra cost. Per-seat pricing is how security tools become more expensive as engineering headcount grows, and we deliberately don't do that.",
    },
    {
      q: "What does the one-off assessment include?",
      a: "A focused, point-in-time code security risk view of a codebase: static analysis, dependency and supply-chain risk, secrets, IaC and container findings, SBOM and license posture, and runtime support status, delivered as a diligence-ready report for $1,500 per project. Source code never leaves the controlled environment, and there is no platform contract attached. Custom report development and investment-committee or underwriting support is available on top for $500-1,500.",
    },
    {
      q: "What does self-hosted mean here?",
      a: "Self-hosted delivery means local code scanning within your controlled environment, integrated with a secure, compliant Azure-hosted processing, analytics and reporting backend. Your source code remains local and is never uploaded to or exposed within the SaaS environment; only the analysis results travel. That's what makes it usable for M&A, regulated, and IP-sensitive contexts. A hosted evaluation environment is available for trials, and SaaS pricing will be announced once it's ready for scale.",
    },
    {
      q: "What do the 'from' prices on the services mean?",
      a: "Third-party software assurance (from $5,000) and the managed code risk assessment service (from $2,500) are scoped engagements. The final price depends on the size of the code, how often it is scanned, and the contract length: once, monthly, quarterly or annual. Send a quotation request with those details and you get a firm quote back.",
    },
    {
      q: "How can we pay?",
      a: "Starter and Growth can be paid by card through secure checkout in the app. Large Enterprise, the one-off assessment, third-party software assurance and the managed service are invoiced and paid by bank transfer, with annual prepayment available on request. Tell us your preferred payment type on the quotation form and the quote will reflect it.",
    },
    {
      q: "What counts against the product / repository limit?",
      a: "The main products or repositories under continuous scanning: Starter covers up to 5, Growth covers 5 to 10, and Large Enterprise is unlimited. The source processing limit (150 MB on Starter, 500 MB on Growth) applies to the size of each codebase processed per scan.",
    },
    {
      q: "What are the contract terms?",
      a: "Starter is available on 3, 6, or 12-month terms. Growth has a 6-month minimum, and Large Enterprise a 12-month minimum. The one-off assessment and Community tier carry no term at all.",
    },
    {
      q: "Can we try the full platform before buying?",
      a: "Yes. Community is free with no card. For a fuller evaluation, request a 30-day guided trial: we set up an isolated workspace with the full scanning stack and walk through the results with your team. Teams moving from Snyk or SonarQube can use the same 30-day trial to compare findings side by side.",
    },
    {
      q: "Is there a free tier?",
      a: "Yes. Community is free for open source, solo developers, and initial evaluation: 3 repositories, 5 scans per month, with SAST, dependency analysis, and secrets detection included.",
    },
    {
      q: "How does this compare to buying separate tools?",
      a: "A typical stack covering SAST, SCA, secrets, IaC, containers, SBOM, and license compliance means several vendors, several contracts, and often per-seat pricing on each. Paxley consolidates those domains into one predictable package between $199 and $999 per month. For 50 developers on 10 repositories that is $499 a month on Growth against roughly $4,750 for a per-seat stack, about 89% less, without exposing source code to multiple third parties.",
    },
  ];

  return (
    <Section id="faq" padding="tight" className="pb-20">
      <Reveal className="mx-auto max-w-3xl text-center">
        <Eyebrow>Pricing FAQ</Eyebrow>
        <Headline size="section" className="mt-4">
          Good <Em>questions.</Em>
        </Headline>
      </Reveal>

      <div className="mx-auto mt-12 max-w-3xl">
        {faqs.map((f) => (
          <details key={f.q} className="group border-b border-[#E4DFD3] py-5">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-base font-semibold [&::-webkit-details-marker]:hidden">
              <span>{f.q}</span>
              <span
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[#E4DFD3] text-sm leading-none opacity-60 transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-open:rotate-45 group-open:border-[#1E4D38]/40 group-open:text-[#1E4D38] group-open:opacity-100"
                aria-hidden="true"
              >
                +
              </span>
            </summary>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed opacity-75">{f.a}</p>
          </details>
        ))}
      </div>
    </Section>
  );
}

// ──────────────────────────────────────────────────────────────────
// Final CTA: green panel
// ──────────────────────────────────────────────────────────────────

function FinalCTA() {
  return (
    <Section container="none" padding="tight" className="pb-20 md:pb-28">
      <Panel tone="green">
        <div className="mx-auto max-w-3xl px-6 py-16 text-center sm:px-10 md:py-20">
          <Reveal>
            <Headline size="section">
              From one risk decision <Em>to continuous assurance.</Em>
            </Headline>
            <Subhead className="mx-auto mt-4 max-w-2xl !opacity-80">
              Start with a $1,500 assessment, a managed service, or a free Community install. When
              you&rsquo;re ready, self-hosted packages from $199 a month cover your whole estate, with
              your source code never leaving your environment.
            </Subhead>
            <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <span className="inline-flex">
                <GradientButton
                  to={ASSESSMENT_LINK}
                  size="lg"
                  className="!border-[#F5F1E6]/30 !bg-[#F5F1E6] !text-[#17150F] hover:!bg-white"
                >
                  Book an assessment
                </GradientButton>
              </span>
              <span className="inline-flex">
                <GhostButton
                  to="/onboarding"
                  size="lg"
                  className="!border-[#F5F1E6]/40 !bg-transparent !text-[#F5F1E6] hover:!bg-white/10"
                >
                  Start free scan
                </GhostButton>
              </span>
            </div>
          </Reveal>
        </div>
      </Panel>
    </Section>
  );
}
